import { useState, useEffect, useMemo, useRef } from "react";
import {
  CreditCard,
  AlertTriangle,
  Loader2,
  X,
  Check,
  Crown,
  Zap,
  Sparkles,
  ArrowRight,
  Calendar,
  Eye,
  PauseCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  getManageBillingLink,
  cancelSubscription,
  checkScheduledCancellation,
  reactivateSubscription,
  getSubscriptionStatus,
  switchPlan,
  migratePlan,
  getProductLink,
  pauseSubscription,
  resumeSubscription,
  type ScheduledCancellationResponse,
  type SubscriptionStatusResponse,
} from "../../services/BillingService";
import type { SettingsTabProps } from "./types";
import { isFreeTier } from "../../utils/userUtils";
import { hasAnyActiveRetailerPlan } from "../../utils/subscriptionGuards";
import {
  getFreeIntentCopy,
  getFreeUserIntent,
  getIntentUpgradeHighlights,
} from "../../utils/freeExperience";
import { track } from "../../utils/analytics";

const BASIC_PRICE_ID = "price_1SpvXMG2cPZes0aXe9CPsPMK";
const PLUS_PRICE_ID = "price_1TfTddG2cPZes0aXmDKAbVOu";
const PRO_PRICE_ID = "price_1SpvZXG2cPZes0aXeCwoqTIa";

type PaywallPlanId = "basic" | "plus" | "pro";

const PAYWALL_PLAN_TIERS: Record<string, number> = { basic: 0, plus: 1, pro: 2 };
const PAYWALL_PLAN_PRICES: Record<string, string> = { basic: "34.99", plus: "59.99", pro: "99.00" };
const PAYWALL_PLAN_NAMES: Record<PaywallPlanId, string> = { basic: "Basic", plus: "Plus", pro: "Pro" };

function formatSubscriptionAmount(amountMinor: number, currency = "usd") {
	try {
		return new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(amountMinor / 100);
	} catch {
		return `$${(amountMinor / 100).toFixed(2)}`;
	}
}

// Exact strings are the server contract (normalizeCancellationReason) and are
// shared with pinto; labels come from translations.
const CANCELLATION_REASONS = [
  "Too expensive",
  "No longer need it",
  "Taking a break",
  "Deals weren't accurate",
  "Missing features",
  "Found a better alternative",
  "Other",
];

// Reasons where pausing is the matched save offer.
const PAUSE_FIRST_REASONS = ["No longer need it", "Taking a break"];
// Reasons that get the free-text box plus the small pause option.
const FEEDBACK_REASONS = ["Missing features", "Found a better alternative", "Other"];
const INACCURATE_REASON = "Deals weren't accurate";
const PRICE_REASON = "Too expensive";

interface Plan {
  id: string;
  name: string;
  price: number;
  priceId: string;
  description: string;
  features: string[];
  highlight?: boolean;
  icon: React.ReactNode;
  badge?: string;
}

const plans: Plan[] = [
  {
    id: "basic",
    name: "Basic",
    price: 34.99,
    priceId: BASIC_PRICE_ID,
    description: "Perfect for getting started",
    icon: <Zap className="w-4 h-4" />,
    features: [
      "1 Walmart + 1 HD Location",
      "Discord Community Access",
      "Push Notifications & Alerts",
      "Favorites & Tracking",
      "Mobile App Access",
      "Product Search & Leads",
    ],
  },
  {
    id: "plus",
    name: "Plus",
    price: 59.99,
    priceId: PLUS_PRICE_ID,
    description: "More stores, more deals",
    icon: <Sparkles className="w-4 h-4" />,
    features: [
      "3 Walmart + 3 HD Locations",
      "Discord Community Access",
      "Push Notifications & Alerts",
      "Favorites & Tracking",
      "Mobile App Access",
      "Product Search & Leads",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 99,
    priceId: PRO_PRICE_ID,
    description: "For serious deal hunters",
    icon: <Crown className="w-4 h-4" />,
    highlight: true,
    badge: "Best Value",
    features: [
      "5 Walmart + 5 HD Locations",
      "5 Target + 5 Dollar General Locations",
      "Advanced local insights + higher limits",
      "Everything in Basic included",
      "Priority Support",
    ],
  },
];

interface BillingTabState {
  subscriptionStatus: SubscriptionStatusResponse | null;
  isLoadingStatus: boolean;
  isLoadingManageBilling: boolean;
  isLoadingAction: string | null;
  showCancelModal: boolean;
  cancelOption: string;
  cancelReason: string;
  isCanceling: boolean;
  scheduledCancellation: ScheduledCancellationResponse | null;
  isLoadingScheduledCancellation: boolean;
  isReactivating: boolean;
  pauseMonths: 1 | 2 | 3;
  isPausing: boolean;
  pausedUntilResult: number | null;
  pauseAccessUntilResult: number | null;
  isResuming: boolean;
}

const PAUSE_MONTH_OPTIONS: Array<1 | 2 | 3> = [1, 2, 3];

function addMonths(from: Date, months: number): Date {
  const d = new Date(from);
  d.setMonth(d.getMonth() + months);
  return d;
}

// A pause starts when the already-paid period ends; periodEnd is Unix seconds.
function pauseStartDate(periodEndSeconds?: number | null): Date {
  const periodEndMs = Number(periodEndSeconds || 0) * 1000;
  return periodEndMs > Date.now() ? new Date(periodEndMs) : new Date();
}

export default function BillingTab({ user, t }: SettingsTabProps) {
  const isFreeUser = isFreeTier(user);
  const hasActivePlan = hasAnyActiveRetailerPlan(user);
  const introOfferDiscountsUsed = user?.introOfferDiscountsUsed ?? 0;
  const isDiscountEligible = introOfferDiscountsUsed < 3;
  const isWelcomeBackOffer = introOfferDiscountsUsed > 0 && isDiscountEligible;
  const [state, setState] = useState<BillingTabState>({
    subscriptionStatus: null,
    isLoadingStatus: true,
    isLoadingManageBilling: false,
    isLoadingAction: null,
    showCancelModal: false,
    cancelOption: "",
    cancelReason: "",
    isCanceling: false,
    scheduledCancellation: null,
    isLoadingScheduledCancellation: true,
    isReactivating: false,
    pauseMonths: 1,
    isPausing: false,
    pausedUntilResult: null,
    pauseAccessUntilResult: null,
    isResuming: false,
  });
  const isRevenueCatUser = String(user?.subscriptionSrc || "").trim().toLowerCase().startsWith("revenuecat");
  const pausedUntilMs = Number(user?.subscriptionPausedUntil) || 0;
  const isPaused = pausedUntilMs > Date.now();
  const pauseAccessUntilMs = Number(user?.subscriptionPauseAccessUntil) || 0;
  const isPauseScheduled = isPaused && pauseAccessUntilMs > Date.now();

  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      try {
        setState((prev) => ({ ...prev, isLoadingStatus: true }));
        const status = await getSubscriptionStatus();
        setState((prev) => ({ ...prev, subscriptionStatus: status }));
      } catch (error) {
        console.error("Failed to load subscription status:", error);
      } finally {
        setState((prev) => ({ ...prev, isLoadingStatus: false }));
      }
    };

    loadSubscriptionStatus();
  }, []);

  useEffect(() => {
    const loadScheduledCancellation = async () => {
      try {
        setState((prev) => ({ ...prev, isLoadingScheduledCancellation: true }));
        const scheduledCancellation = await checkScheduledCancellation();
        setState((prev) => ({ ...prev, scheduledCancellation }));
      } catch (error) {
        console.error("Error loading scheduled cancellation status:", error);
        setState((prev) => ({
          ...prev,
          scheduledCancellation: {
            hasScheduledCancellation: false,
            cancelAtPeriodEnd: false,
            currentPeriodEnd: null,
            daysRemaining: 0,
          },
        }));
      } finally {
        setState((prev) => ({ ...prev, isLoadingScheduledCancellation: false }));
      }
    };

    loadScheduledCancellation();
  }, []);

  // Deep link from the cancel-save email: /settings?tab=billing&action=pause
  // opens the cancel modal with the pause card showing (nothing is submitted).
  const pauseDeepLinkRef = useRef(false);
  const pauseDeepLinkHandledRef = useRef(false);

  useEffect(() => {
    if (state.showCancelModal) {
      if (pauseDeepLinkRef.current) {
        pauseDeepLinkRef.current = false;
        setState((prev) => ({ ...prev, cancelOption: "No longer need it", cancelReason: "" }));
        return;
      }
      setState((prev) => ({ ...prev, cancelOption: "", cancelReason: "" }));
    }
  }, [state.showCancelModal]);

  useEffect(() => {
    if (pauseDeepLinkHandledRef.current) return;
    if (state.isLoadingStatus || state.isLoadingScheduledCancellation) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("action") !== "pause") return;
    pauseDeepLinkHandledRef.current = true;
    params.delete("action");
    const query = params.toString();
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
    if (isPaused || state.scheduledCancellation?.hasScheduledCancellation) return;
    const isStripeSubscriber = !isFreeUser && !!state.subscriptionStatus?.hasSubscription;
    if (!isStripeSubscriber && !isRevenueCatUser) return;
    track("cancellation_flow_opened", { plan_id: state.subscriptionStatus?.currentPlan || "unknown", billing_provider: isRevenueCatUser ? "revenuecat" : "stripe" });
    pauseDeepLinkRef.current = true;
    setState((prev) => ({ ...prev, showCancelModal: true }));
  }, [state.isLoadingStatus, state.isLoadingScheduledCancellation, state.subscriptionStatus, state.scheduledCancellation, isPaused, isFreeUser, isRevenueCatUser]);

  const handleMigratePlan = async (targetPlan: PaywallPlanId) => {
    setState((prev) => ({ ...prev, isLoadingAction: targetPlan }));
    try {
      const response = await migratePlan(targetPlan);
      if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      }
    } catch (error) {
      console.error("Failed to migrate plan:", error);
      toast.error(t("products.failedToMigratePlan"));
    } finally {
      setState((prev) => ({ ...prev, isLoadingAction: null }));
    }
  };

  // Handle upgrading from free tier
  const handleFreeTierUpgrade = async (targetPlan: PaywallPlanId) => {
    if (hasActivePlan) {
      toast.error(t("products.alreadyHaveActivePlan"));
      return;
    }
    setState((prev) => ({ ...prev, isLoadingAction: targetPlan }));
    try {
      const priceId =
        targetPlan === "pro" ? PRO_PRICE_ID : targetPlan === "plus" ? PLUS_PRICE_ID : BASIC_PRICE_ID;
      // Do not pass billing_tab source so intro discount applies for eligible free users.
      const response = await getProductLink([priceId]);
      if (response?.link) {
        window.location.href = response.link;
      } else {
        throw new Error("No checkout link received");
      }
    } catch (error) {
      console.error("Failed to start checkout:", error);
      toast.error(t("products.failedToStartCheckout"));
    } finally {
      setState((prev) => ({ ...prev, isLoadingAction: null }));
    }
  };

  const handleSwitchPlan = async (targetPlan: PaywallPlanId, cancelFlowReason?: string) => {
    setState((prev) => ({ ...prev, isLoadingAction: targetPlan }));
    try {
      const response = await switchPlan(
        targetPlan,
        cancelFlowReason !== undefined ? { source: "cancel_flow", reason: cancelFlowReason } : undefined
      );
      if (response.action === "redirect" && response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      } else if (response.action === "scheduled") {
        toast.success(
          cancelFlowReason !== undefined
            ? t("products.cancelStepDownScheduled").replace("{{plan}}", PAYWALL_PLAN_NAMES[targetPlan])
            : response.message
        );
        // Reload to show updated status
        window.location.reload();
      }
    } catch (error) {
      console.error("Failed to switch plan:", error);
      toast.error(t("products.failedToChangePlan"));
    } finally {
      setState((prev) => ({ ...prev, isLoadingAction: null }));
    }
  };

  const handleManageBilling = async () => {
    setState((prev) => ({ ...prev, isLoadingManageBilling: true }));
    try {
      const result = await getManageBillingLink();
      if (result.link) {
        window.location.href = result.link;
      } else {
        toast.error(t("settings.manageBillingError"));
        setState((prev) => ({ ...prev, isLoadingManageBilling: false }));
      }
    } catch (error) {
      console.error("Error getting manage billing link:", error);
      toast.error(t("settings.manageBillingError"));
      setState((prev) => ({ ...prev, isLoadingManageBilling: false }));
    }
  };

  const handleCancelAll = () => {
		track("cancellation_flow_opened", { plan_id: state.subscriptionStatus?.currentPlan || "unknown", billing_provider: "stripe" });
    setState((prev) => ({ ...prev, showCancelModal: true, cancelOption: "", cancelReason: "" }));
  };

	// Step-down target for the "Too expensive" offer: Pro→Plus, Plus→Basic.
	// Basic, legacy and plan-less subscribers get the pause option instead.
	const cancelFlowDowngradeTarget: PaywallPlanId | null = state.subscriptionStatus?.isLegacyPlan
		? null
		: state.subscriptionStatus?.currentPlan === "pro"
			? "plus"
			: state.subscriptionStatus?.currentPlan === "plus"
				? "basic"
				: null;

	// The save offer matched to a reason (null = no offer for that reason).
	const offerForReason = (reason: string): { offerType: string; targetPlan?: PaywallPlanId } | null => {
		if (reason === PRICE_REASON) {
			return cancelFlowDowngradeTarget ? { offerType: "downgrade", targetPlan: cancelFlowDowngradeTarget } : { offerType: "pause" };
		}
		if (PAUSE_FIRST_REASONS.includes(reason)) return { offerType: "pause" };
		return null;
	};

	const handleReasonSelected = (reason: string) => {
		const planId = state.subscriptionStatus?.currentPlan || "unknown";
		track("cancellation_reason_selected", { reason_category: reason, plan_id: planId });
		const offer = isRevenueCatUser ? null : offerForReason(reason);
		if (offer) {
			track("retention_offer_viewed", { reason_category: reason, offer_type: offer.offerType, plan_id: planId, ...(offer.targetPlan ? { target_plan: offer.targetPlan } : {}) });
		}
		setState((prev) => ({ ...prev, cancelOption: reason, cancelReason: "" }));
	};

	const handleRetentionDowngrade = async (targetPlan: PaywallPlanId) => {
		const planId = state.subscriptionStatus?.currentPlan || "unknown";
		const reason = state.cancelOption;
		track("retention_offer_accepted", { reason_category: reason, offer_type: "downgrade", plan_id: planId, target_plan: targetPlan });
		await handleSwitchPlan(targetPlan, reason);
	};

  const handleCancelSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.cancelOption) {
      toast.error(t("products.selectCancelReason"));
      return;
    }
    setState((prev) => ({ ...prev, isCanceling: true }));
		const skippedOffer = isRevenueCatUser ? null : offerForReason(state.cancelOption);
		if (skippedOffer) {
			track("retention_offer_skipped", { reason_category: state.cancelOption, offer_type: skippedOffer.offerType, plan_id: state.subscriptionStatus?.currentPlan || "unknown", ...(skippedOffer.targetPlan ? { target_plan: skippedOffer.targetPlan } : {}) });
		}
		track("cancellation_submitted", { reason_category: state.cancelOption, plan_id: state.subscriptionStatus?.currentPlan || "unknown", billing_provider: "stripe" });
    try {
      await cancelSubscription(state.cancelOption, state.cancelReason);
			track("cancellation_succeeded", { reason_category: state.cancelOption, plan_id: state.subscriptionStatus?.currentPlan || "unknown", billing_provider: "stripe" });
      window.location.reload();
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error(t("products.failedToCancelSubscription"));
			track("cancellation_error", { reason_category: state.cancelOption, plan_id: state.subscriptionStatus?.currentPlan || "unknown", billing_provider: "stripe" });
      setState((prev) => ({ ...prev, isCanceling: false }));
    }
  };

  const handlePauseSubscription = async () => {
    const months = state.pauseMonths;
    setState((prev) => ({ ...prev, isPausing: true }));
    try {
      const result = await pauseSubscription(months, state.cancelOption || undefined);
      track("retention_offer_accepted", { reason_category: state.cancelOption || "none", offer_type: `pause_${months}m`, plan_id: state.subscriptionStatus?.currentPlan || "unknown" });
      const accessStart = pauseStartDate(state.subscriptionStatus?.periodEnd);
      setState((prev) => ({
        ...prev,
        isPausing: false,
        pausedUntilResult: result?.pausedUntil || addMonths(accessStart, months).getTime(),
        pauseAccessUntilResult: result?.accessUntil || accessStart.getTime(),
      }));
    } catch (error) {
      console.error("Error pausing subscription:", error);
      const message = error instanceof Error && error.message ? error.message : "";
      toast.error(message && message.length < 200 ? message : t("products.failedToPause"));
      track("retention_offer_error", { reason_category: state.cancelOption || "none", offer_type: `pause_${months}m`, plan_id: state.subscriptionStatus?.currentPlan || "unknown" });
      setState((prev) => ({ ...prev, isPausing: false }));
    }
  };

  const handleResumeSubscription = async () => {
    setState((prev) => ({ ...prev, isResuming: true }));
    try {
      await resumeSubscription();
      track("subscription_reactivated", { plan_id: state.subscriptionStatus?.currentPlan || "unknown", billing_provider: "stripe" });
      toast.success(t("products.subscriptionResumed"));
      window.location.reload();
    } catch (error) {
      console.error("Error resuming subscription:", error);
      toast.error(t("products.failedToResume"));
      setState((prev) => ({ ...prev, isResuming: false }));
    }
  };

  const handleReactivateSubscription = async () => {
    setState((prev) => ({ ...prev, isReactivating: true }));
    try {
      await reactivateSubscription();
			track("subscription_reactivated", { plan_id: state.subscriptionStatus?.currentPlan || "unknown", billing_provider: "stripe" });
      window.location.reload();
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      toast.error(t("products.failedToReactivate"));
      setState((prev) => ({ ...prev, isReactivating: false }));
    }
  };

  // Hooks must run unconditionally to preserve call order across renders
  const _intent = getFreeUserIntent();
  const _intentCopy = useMemo(() => getFreeIntentCopy(_intent), [_intent]);
  const _intentHighlights = useMemo(() => getIntentUpgradeHighlights(_intent), [_intent]);
  void _intentCopy; void _intentHighlights;

  const cancelReasonLabels: Record<string, string> = {
    "Too expensive": t("products.tooExpensive"),
    "Found a better alternative": t("products.foundBetterAlternative"),
    "Missing features": t("products.missingFeatures"),
    "No longer need it": t("products.noLongerNeedIt"),
    "Taking a break": t("products.takingABreak"),
    "Deals weren't accurate": t("products.dealsWerentAccurate"),
    "Other": t("products.other"),
  };

  if (state.isLoadingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-dark-300">{t("products.loadingBilling")}</p>
        </div>
      </div>
    );
  }

  const { subscriptionStatus } = state;
  const currentPlan = subscriptionStatus?.currentPlan || "none";
  const isLegacyPlan = subscriptionStatus?.isLegacyPlan || false;
  const hasSubscription = subscriptionStatus?.hasSubscription || false;
	const downgradeTarget = cancelFlowDowngradeTarget;

  const pausedNotice = isPaused ? (
    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <PauseCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <div>
            {isPauseScheduled ? (
              <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                {t("products.subscriptionPauseScheduled")
                  .replace("{{accessDate}}", new Date(pauseAccessUntilMs).toLocaleDateString())
                  .replace("{{date}}", new Date(pausedUntilMs).toLocaleDateString())}
              </p>
            ) : (
              <>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                  {t("products.subscriptionPausedUntil").replace("{{date}}", new Date(pausedUntilMs).toLocaleDateString())}
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                  {t("products.subscriptionPausedNote")}
                </p>
              </>
            )}
          </div>
        </div>
        <button
          onClick={handleResumeSubscription}
          disabled={state.isResuming}
          className="ml-4 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
        >
          {state.isResuming ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              {t("products.resuming")}
            </>
          ) : (
            t("products.resumeNow")
          )}
        </button>
      </div>
    </div>
  ) : null;

  const formatPauseDuration = (months: number) =>
    months === 1 ? t("products.pauseOneMonth") : t("products.pauseMonths").replace("{{count}}", String(months));

  const renderPauseOption = (prominent: boolean) => {
    if (isRevenueCatUser) {
      return (
        <div className="rounded-lg bg-gray-50 dark:bg-dark-700/50 p-3 text-xs text-gray-600 dark:text-dark-300">
          {t("products.pauseManagedInAppStore")}
        </div>
      );
    }
    const accessStart = pauseStartDate(state.subscriptionStatus?.periodEnd);
    const accessDate = accessStart.toLocaleDateString();
    const resumeDate = addMonths(accessStart, state.pauseMonths).toLocaleDateString();
    const duration = formatPauseDuration(state.pauseMonths);
    return (
      <div
        className={`rounded-xl border p-3 space-y-2 ${
          prominent
            ? "border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20"
            : "border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700/40"
        }`}
      >
        <div className="flex items-center gap-2">
          <PauseCircle className={`w-4 h-4 ${prominent ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-dark-300"}`} />
          <p className={`text-sm font-semibold ${prominent ? "text-blue-900 dark:text-blue-200" : "text-gray-800 dark:text-dark-100"}`}>
            {t("products.pauseInstead")}
          </p>
        </div>
        {prominent && (
          <p className="text-xs text-blue-800 dark:text-blue-300">{t("products.pauseInsteadHint")}</p>
        )}
        <div className="grid grid-cols-3 gap-2" role="radiogroup">
          {PAUSE_MONTH_OPTIONS.map((months) => {
            const selected = state.pauseMonths === months;
            return (
              <button
                key={months}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setState((prev) => ({ ...prev, pauseMonths: months }))}
                className={`px-2 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                  selected
                    ? "border-blue-500 bg-white dark:bg-dark-800 text-blue-700 dark:text-blue-300"
                    : "border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-600 dark:text-dark-300"
                }`}
              >
                {formatPauseDuration(months)}
              </button>
            );
          })}
        </div>
        <p className={`text-xs ${prominent ? "text-blue-800 dark:text-blue-300" : "text-gray-600 dark:text-dark-300"}`}>
          {t("products.pauseBillingDescription")
            .replace("{{accessDate}}", accessDate)
            .replace("{{duration}}", duration)
            .replace("{{date}}", resumeDate)}
        </p>
        <button
          type="button"
          onClick={handlePauseSubscription}
          disabled={state.isPausing}
          className={`w-full px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 ${
            prominent
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-500 text-gray-800 dark:text-dark-100"
          }`}
        >
          {state.isPausing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t("products.pausing")}
            </>
          ) : (
            t("products.pauseForDuration").replace("{{duration}}", duration)
          )}
        </button>
      </div>
    );
  };

  // Free tier rendering
  if (isFreeUser) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-xl">
            <Eye className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {t("products.freePreview")}
            </h3>
            <p className="text-xs text-gray-600 dark:text-dark-400">
              {t("products.freePreviewUpgradeHint")}
            </p>
          </div>
        </div>

        {pausedNotice}

        {isDiscountEligible && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
              {isWelcomeBackOffer ? t("products.welcomeBack") : t("products.limitedTime")} {t("products.halfOffFirstMonth")}
            </p>
          </div>
        )}

        {/* Compact Free vs Paid */}
        <div className="rounded-xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 overflow-hidden">
          {[
            { label: t("products.onlineDealsRow"), free: t("products.freeOnlineDeals"), paid: t("products.paidOnlineDeals") },
            { label: t("products.localStoresRow"), free: t("products.freeLocalStores"), paid: t("products.paidLocalStores") },
            { label: t("products.alertsSavesRow"), free: t("products.freeAlertsSaves"), paid: t("products.paidAlertsSaves") },
            { label: t("products.productSearchRow"), free: t("products.freeProductSearch"), paid: t("products.paidProductSearch") },
          ].map((row, idx) => (
            <div key={idx} className={`flex items-center text-xs px-3 py-2 ${idx < 3 ? "border-b border-gray-100 dark:border-dark-700/50" : ""}`}>
              <span className="flex-1 text-gray-600 dark:text-dark-300">{row.label}</span>
              <span className="w-20 text-center text-gray-400 dark:text-dark-500">{row.free}</span>
              <span className="w-20 text-center font-semibold text-purple-600 dark:text-purple-400">{row.paid}</span>
            </div>
          ))}
        </div>

        {/* Plan Options */}
        <div className="space-y-3">
          {plans.map((plan) => {
            const isLoading = state.isLoadingAction === plan.id;
            return (
              <div
                key={plan.id}
                onClick={() => !isLoading && handleFreeTierUpgrade(plan.id as PaywallPlanId)}
                className={`relative rounded-xl p-4 transition-all cursor-pointer active:scale-[0.99] ${
                  plan.highlight
                    ? "bg-white dark:bg-dark-800 border-2 border-purple-500 shadow-md shadow-purple-50 dark:shadow-purple-900/20"
                    : "bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 shadow-sm"
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-2.5 left-4 bg-purple-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">
                    {plan.badge === "Best Value" ? t("products.bestValue") : plan.badge}
                  </div>
                )}

                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${plan.highlight ? "bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400" : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-dark-300"}`}>
                      {plan.icon}
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-gray-900 dark:text-white">{plan.name}</h2>
                      <p className="text-xs text-gray-500 dark:text-dark-400">{plan.description}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-3 pb-3 border-b border-gray-100 dark:border-dark-700">
                  {isDiscountEligible ? (
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-sm line-through text-gray-400 dark:text-dark-500">${plan.price.toFixed(2)}</span>
                      <span className="text-2xl font-black text-gray-900 dark:text-white">${(plan.price * 0.5).toFixed(2)}</span>
                      <span className="text-sm text-gray-500 dark:text-dark-400">{t("products.firstMonth")}</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-black text-gray-900 dark:text-white">${plan.price.toFixed(2)}</span>
                      <span className="text-sm text-gray-500 dark:text-dark-400">{t("products.perMo")}</span>
                    </div>
                  )}
                </div>

                <ul className="space-y-2 mb-4">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlight ? "text-purple-500" : "text-green-500"}`} />
                      <span className="text-sm text-gray-700 dark:text-dark-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  disabled={isLoading}
                  className={`w-full py-3 rounded-lg font-semibold text-sm transition-all ${
                    plan.highlight ? "bg-purple-500 text-white active:bg-purple-600" : "bg-gray-900 dark:bg-primary-600 text-white active:bg-gray-800 dark:active:bg-primary-700"
                  } ${isLoading ? "opacity-60" : ""}`}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("common.loading")}
                    </span>
                  ) : (
                    t("products.unlockPlan").replace("{{name}}", plan.name)
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-gray-400 dark:text-dark-500 text-xs">
          {t("products.cancelAnytimeStripe")}
        </p>
      </div>
    );
  }

  // Render plan card for selection/migration
  const renderPlanCard = (plan: Plan, action: "migrate" | "switch" | "select") => {
    const isCurrentPlan = currentPlan === plan.id;
    const isLoading = state.isLoadingAction === plan.id;
    const isUpgrade = (PAYWALL_PLAN_TIERS[plan.id] ?? 0) > (PAYWALL_PLAN_TIERS[currentPlan] ?? 0);

    const handleClick = () => {
      if (isLoading || isCurrentPlan) return;
      if (action === "migrate") {
        handleMigratePlan(plan.id as PaywallPlanId);
      } else if (action === "switch") {
        handleSwitchPlan(plan.id as PaywallPlanId);
      }
    };

    return (
      <div
        key={plan.id}
        onClick={handleClick}
        className={`relative rounded-xl p-4 transition-all cursor-pointer ${
          isCurrentPlan
            ? "bg-white dark:bg-dark-800 border-2 border-green-500 shadow-md"
            : plan.highlight
            ? "bg-white dark:bg-dark-800 border-2 border-purple-500 shadow-md shadow-purple-50 dark:shadow-purple-900/20 active:scale-[0.99]"
            : "bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 shadow-sm active:scale-[0.99]"
        } ${isCurrentPlan ? "cursor-default" : ""}`}
      >
        {/* Badge */}
        {isCurrentPlan ? (
          <div className="absolute -top-2.5 left-4 bg-green-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">
            {t("products.currentPlan")}
          </div>
        ) : plan.badge ? (
          <div className="absolute -top-2.5 left-4 bg-purple-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full">
            {plan.badge === "Best Value" ? t("products.bestValue") : plan.badge}
          </div>
        ) : null}

        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className={`p-2 rounded-lg ${
                isCurrentPlan
                  ? "bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400"
                  : plan.highlight
                  ? "bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400"
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-dark-300"
              }`}
            >
              {plan.icon}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">{plan.name}</h2>
              <p className="text-xs text-gray-500 dark:text-dark-400">{plan.description}</p>
            </div>
          </div>
        </div>

        {/* Price */}
        <div className="mb-3 pb-3 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-black text-gray-900 dark:text-white">
              ${plan.price.toFixed(2)}
            </span>
            <span className="text-sm text-gray-500 dark:text-dark-400">{t("products.perMo")}</span>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-2 mb-4">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <Check
                className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                  isCurrentPlan
                    ? "text-green-500"
                    : plan.highlight
                    ? "text-purple-500"
                    : "text-green-500"
                }`}
              />
              <span className="text-sm text-gray-700 dark:text-dark-300">{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        {!isCurrentPlan && (
          <button
            disabled={isLoading}
            className={`w-full py-3 rounded-lg font-semibold text-sm transition-all ${
              plan.highlight
                ? "bg-purple-500 text-white active:bg-purple-600"
                : "bg-gray-900 dark:bg-primary-600 text-white active:bg-gray-800 dark:active:bg-primary-700"
            } ${isLoading ? "opacity-60" : ""}`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("common.loading")}
              </span>
            ) : action === "migrate" ? (
              <span className="flex items-center justify-center gap-2">
                {t("products.moveTo").replace("{{name}}", plan.name)}
                <ArrowRight className="w-4 h-4" />
              </span>
            ) : isUpgrade ? (
              t("products.upgradeToPro").replace("Pro", plan.name)
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                {t("products.downgradeAtPeriodEnd")}
              </span>
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <CreditCard className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {t("products.manageYourPlan")}
          </h3>
          <p className="text-xs text-gray-600 dark:text-dark-400">
            {isLegacyPlan
              ? t("products.upgradeToNewPlans")
              : t("products.updateSubscription")}
          </p>
        </div>
      </div>

      {pausedNotice}

      {/* Scheduled Cancellation Banner */}
      {state.scheduledCancellation?.hasScheduledCancellation && (
        <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-900 dark:text-orange-300">
                  {t("products.scheduledCancellation")}
                </p>
                <p className="text-xs text-orange-700 dark:text-orange-400 mt-0.5">
                  {t("products.subscriptionEndsIn")
                    .replace("{{days}}", String(state.scheduledCancellation.daysRemaining))
                    .replace("{{date}}", state.scheduledCancellation.currentPeriodEnd
                      ? new Date(state.scheduledCancellation.currentPeriodEnd * 1000).toLocaleDateString()
                      : "")}
                </p>
              </div>
            </div>
            <button
              onClick={handleReactivateSubscription}
              disabled={state.isReactivating}
              className="ml-4 px-3 py-1.5 bg-orange-600 text-white text-xs font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
            >
              {state.isReactivating ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {t("products.reactivating")}
                </>
              ) : (
                t("products.reactivate")
              )}
            </button>
          </div>
        </div>
      )}

      {/* Legacy Plan Migration */}
      {isLegacyPlan && (
        <div className="space-y-4">
          {/* Current Legacy Plan Info */}
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                  {t("products.newPlansAvailable")}
                </h4>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  {t("products.newPlansDescription")}
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {subscriptionStatus?.retailers?.map((retailer) => (
                    <span
                      key={retailer}
                      className="inline-block px-2 py-0.5 bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 text-xs rounded-full capitalize"
                    >
                      {retailer.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* New Plan Options */}
          <div className="space-y-3">
            {plans.map((plan) => renderPlanCard(plan, "migrate"))}
          </div>
        </div>
      )}

      {/* New Plan Management (Basic or Pro) */}
      {!isLegacyPlan && hasSubscription && ["basic", "plus", "pro"].includes(currentPlan) && (
        <div className="space-y-4">
          {/* Current Plan Display */}
          <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
                  {currentPlan === "pro" ? (
                    <Crown className="w-5 h-5 text-green-600 dark:text-green-400" />
                  ) : currentPlan === "plus" ? (
                    <Sparkles className="w-5 h-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-900 dark:text-green-300 capitalize">
                    {currentPlan} Plan
                  </h4>
                  <p className="text-xs text-green-700 dark:text-green-400">
					{formatSubscriptionAmount(subscriptionStatus?.nextChargeAmount ?? subscriptionStatus?.monthlyAmount ?? Math.round(Number(PAYWALL_PLAN_PRICES[currentPlan] || 0) * 100), subscriptionStatus?.currency)}/{subscriptionStatus?.billingInterval || "month"}
                    {subscriptionStatus?.periodEnd && (
                      <> • {t("products.renewsOn").replace("{{date}}", new Date(subscriptionStatus.periodEnd * 1000).toLocaleDateString())}</>
                    )}
                  </p>
                  {subscriptionStatus?.hasActiveDiscount && subscriptionStatus.discountPercent && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1">
                      🎉 {t("products.discountApplied").replace("{{percent}}", String(subscriptionStatus.discountPercent))}
                    </p>
                  )}
                </div>
              </div>
              <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>

          {/* Upgrade/Downgrade Options */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-dark-200">
              {(PAYWALL_PLAN_TIERS[currentPlan] ?? 0) < PAYWALL_PLAN_TIERS.pro ? t("products.upgradeYourPlan") : t("products.changeYourPlan")}
            </h4>
            {plans
              .filter((p) => p.id !== currentPlan)
              .map((plan) => renderPlanCard(plan, "switch"))}
          </div>

          {/* Info about downgrade */}
          {(PAYWALL_PLAN_TIERS[currentPlan] ?? 0) > PAYWALL_PLAN_TIERS.basic && (
            <p className="text-xs text-gray-500 dark:text-dark-400 text-center">
              {t("products.downgradeNote")}
            </p>
          )}
        </div>
      )}

      {/* No Subscription - Show Plans */}
      {!hasSubscription && (
        <div className="space-y-4">
          <div className="text-center mb-3">
            <p className="text-sm text-gray-600 dark:text-dark-300">{t("products.choosePlanToStart")}</p>
          </div>
          <div className="space-y-3">
            {plans.map((plan) => renderPlanCard(plan, "migrate"))}
          </div>
        </div>
      )}


      {/* Action Buttons */}
      {hasSubscription && (
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-dark-700">
          <button
            onClick={handleManageBilling}
            disabled={state.isLoadingManageBilling}
            className={`flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
              state.isLoadingManageBilling
                ? "bg-gray-100 dark:bg-dark-700 text-gray-400 dark:text-dark-500 cursor-not-allowed"
                : "bg-white dark:bg-dark-800 border-2 border-gray-200 dark:border-dark-600 text-gray-700 dark:text-dark-200 hover:border-gray-300 dark:hover:border-dark-500 hover:bg-gray-50 dark:hover:bg-dark-700"
            }`}
          >
            {state.isLoadingManageBilling ? (
              <>
                <Loader2 className="animate-spin h-4 w-4" />
                {t("settings.loading")}
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4" />
                {t("products.paymentMethod")}
              </>
            )}
          </button>

          {!state.scheduledCancellation?.hasScheduledCancellation && (
            <button
              onClick={handleCancelAll}
              className="flex-1 px-4 py-3 bg-white dark:bg-dark-800 border-2 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:border-red-300 dark:hover:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 font-medium text-sm flex items-center justify-center gap-2"
            >
              <AlertTriangle className="h-4 w-4" />
              {t("products.cancelSubscription")}
            </button>
          )}
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {state.showCancelModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[10000] overflow-y-auto pb-24 md:pb-4"
          onClick={() => {
            if (state.pausedUntilResult) {
              window.location.reload();
              return;
            }
            setState((prev) => ({
              ...prev,
              showCancelModal: false,
              cancelOption: "",
              cancelReason: "",
            }));
          }}
        >
          <div
            className="bg-white dark:bg-dark-800 rounded-2xl max-w-sm w-full p-5 shadow-2xl my-auto max-h-[90vh] overflow-y-auto relative z-[10001] border border-gray-200 dark:border-dark-700"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                if (state.pausedUntilResult) {
                  window.location.reload();
                  return;
                }
                setState((prev) => ({
                  ...prev,
                  showCancelModal: false,
                  cancelOption: "",
                  cancelReason: "",
                }));
              }}
              className="absolute top-4 right-4 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 transition-colors p-1 z-10"
            >
              <X size={20} />
            </button>

            {state.pausedUntilResult ? (
              <div className="text-center space-y-3 py-2">
                <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
                  <PauseCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("products.pauseConfirmTitle")}
                </h2>
                <p className="text-sm text-gray-600 dark:text-dark-300">
                  {t("products.pauseConfirmBody")
                    .replace("{{accessDate}}", new Date(state.pauseAccessUntilResult || Date.now()).toLocaleDateString())
                    .replace("{{date}}", new Date(state.pausedUntilResult).toLocaleDateString())}
                </p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  {t("common.done")}
                </button>
              </div>
            ) : (
            <>
            <div className="text-center mb-4">
              <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mb-3">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                {t("products.cancelSubscriptionQuestion")}
              </h2>
              <p className="text-xs text-gray-500 dark:text-dark-400">
                {t("products.keepAccessUntilEnd")}
              </p>
            </div>

            <form onSubmit={handleCancelSubscription} className="space-y-4">
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-gray-900 dark:text-white text-left mb-2">
                  {t("products.whyAreYouLeaving")}
                </legend>
                <div className="space-y-1.5">
                  {CANCELLATION_REASONS.map((reason) => {
                    const isSelected = state.cancelOption === reason;
                    return (
                      <label
                        key={reason}
                        className={`flex items-center gap-3 p-2.5 border rounded-lg cursor-pointer transition-colors text-left ${
                          isSelected
                            ? "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30"
                            : "border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700"
                        }`}
                      >
                        <input
                          type="radio"
                          name="cancelOption"
                          value={reason}
                          checked={isSelected}
                          onChange={(e) => e.target.checked && handleReasonSelected(e.target.value)}
                          className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-dark-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-dark-200">{cancelReasonLabels[reason] || reason}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

				{state.cancelOption === PRICE_REASON && downgradeTarget && !isRevenueCatUser && (
					<div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-2">
						<p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">{t("products.cancelStepDownTitle")}</p>
						<p className="text-xs text-emerald-800 dark:text-emerald-300">
							{t("products.cancelStepDownBody")
								.replace("{{plan}}", PAYWALL_PLAN_NAMES[downgradeTarget])
								.replace("{{price}}", `$${PAYWALL_PLAN_PRICES[downgradeTarget]}`)}
						</p>
						<button
							type="button"
							onClick={() => void handleRetentionDowngrade(downgradeTarget)}
							disabled={state.isLoadingAction === downgradeTarget}
							className="w-full px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
						>
							{state.isLoadingAction === downgradeTarget ? (
								<Loader2 className="w-4 h-4 animate-spin" />
							) : (
								t("products.cancelStepDownButton").replace("{{plan}}", PAYWALL_PLAN_NAMES[downgradeTarget])
							)}
						</button>
					</div>
				)}

				{state.cancelOption === PRICE_REASON && !downgradeTarget && renderPauseOption(true)}

				{PAUSE_FIRST_REASONS.includes(state.cancelOption) && renderPauseOption(true)}

				{PAUSE_FIRST_REASONS.includes(state.cancelOption) && (
					<div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-800 dark:text-blue-200">
						{t("products.cancelSetupSavedNote")}
					</div>
				)}

				{(FEEDBACK_REASONS.includes(state.cancelOption) || state.cancelOption === INACCURATE_REASON) && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-dark-200 mb-1">
						{state.cancelOption === "Missing features"
							? t("products.cancelWhichFeature")
							: state.cancelOption === "Found a better alternative"
								? t("products.cancelWhatWorkedBetter")
								: state.cancelOption === INACCURATE_REASON
									? t("products.cancelWhichDealWasOff")
									: t("products.pleaseTellUsMore")}{" "}
						<span className="text-gray-400">{t("products.optionalLabel")}</span>
                  </label>
                  <textarea
                    value={state.cancelReason}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        cancelReason: e.target.value,
                      }))
                    }
                    placeholder={state.cancelOption === INACCURATE_REASON ? t("products.cancelWhichDealWasOffPlaceholder") : t("products.enterYourReason")}
                    rows={2}
					maxLength={500}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-400 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none text-sm"
                  />
                </div>
              )}

				{state.cancelOption === INACCURATE_REASON && (
					<p className="text-xs text-gray-600 dark:text-dark-300">{t("products.cancelInaccurateThanks")}</p>
				)}

              {(state.cancelOption === "" || FEEDBACK_REASONS.includes(state.cancelOption)) && renderPauseOption(false)}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={state.isCanceling || !state.cancelOption}
                  className="w-full px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer font-medium text-sm flex items-center justify-center gap-2"
                >
                  {state.isCanceling ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("products.canceling")}
                    </>
                  ) : (
                    t("products.cancelSubscription")
                  )}
                </button>
              </div>
            </form>
            </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
